import { computed, inject, Signal } from '@angular/core';
import { getAddressStreetAndPostCodeFormatted } from '@mwe/utils';
import { UmmeldungStore } from '../ummeldung.store';
import { Ummeldung } from '../../../../generated/model/ummeldung.model';
import {
  ROUTE_UMMELDUNG_CURRENT_CONTRACT_PARTNER,
  ROUTE_UMMELDUNG_DATE,
  ROUTE_UMMELDUNG_NEW_CONTRACT_PARTNER,
  ROUTE_UMMELDUNG_PRODUCT,
} from '../../../app.constants';
import {
  bisherigerVertragspartnerStepPflichtfelderSchema,
  bisherigerVertragspartnerStepSchema,
  datumSchema,
  produktSchema,
} from '../schemas';
import {
  neuerVertragspartnerAktiverZaehlpunktPflichtfelderSchema,
  neuerVertragspartnerAktiverZaehlpunktSchema,
  neuerVertragspartnerInaktiverZaehlpunktPflichtfelderSchema,
  neuerVertragspartnerInaktiverZaehlpunktSchema,
} from '../schemas/neuer-vertragspartner.schema';
import { isDatumStepComplete, isDraftValid, isProduktStepComplete } from './step-completeness.util';
import { mapProduktdatenToFormValue } from './produkt-form.mapper';
import { mapUmmeldungToDatumFormValue } from './ummelde-datum.mapper';
import { mapBisherigerKundeToStepFormValue } from './bisheriger-vertragspartner.mapper';
import { mapNeuerKundeToStepFormValue } from './neuer-vertragspartner.mapper';
import { mapUmmeldungAdresseToAddress } from './ummeldung-mapper';

export type SummaryStepId = 'produkt' | 'datum' | 'bisherigerVertragspartner' | 'neuerVertragspartner';

export interface SummaryStep {
  id: SummaryStepId;
  titleKey: string;
  isComplete: boolean;
  route: string;
}

export const SUMMARY_STEPS: Omit<SummaryStep, 'isComplete'>[] = [
  {
    id: 'produkt',
    titleKey: 'ummeldung.step.summary.steps.produkt',
    route: ROUTE_UMMELDUNG_PRODUCT,
  },
  {
    id: 'datum',
    titleKey: 'ummeldung.step.summary.steps.datum',
    route: ROUTE_UMMELDUNG_DATE,
  },
  {
    id: 'bisherigerVertragspartner',
    titleKey: 'ummeldung.step.summary.steps.bisherigerVertragspartner',
    route: ROUTE_UMMELDUNG_CURRENT_CONTRACT_PARTNER,
  },
  {
    id: 'neuerVertragspartner',
    titleKey: 'ummeldung.step.summary.steps.neuerVertragspartner',
    route: ROUTE_UMMELDUNG_NEW_CONTRACT_PARTNER,
  },
];

/**
 * Whether each of steps 1–4 is complete, judged from the store by the same draft and the same rules
 * the step applies itself. Steps 3 and 4 also need their signature — except for an unknown bisherige*r
 * Vertragspartner*in, who cannot sign. Needs an injection context.
 */
export function injectSummaryStepCompleteness(): Signal<Record<SummaryStepId, boolean>> {
  const store = inject(UmmeldungStore);
  const ummeldung = store.activeUmmeldung;
  const bisherigerVertragspartnerMode = store.bisherigerVertragspartnerMode();

  const isProduktDraftValid = isDraftValid(() => mapProduktdatenToFormValue(ummeldung()?.produktdaten ?? []), produktSchema);
  const isDatumDraftValid = isDraftValid(() => mapUmmeldungToDatumFormValue(ummeldung()), datumSchema);

  const bisherigerVertragspartnerDraft = computed(() => mapBisherigerKundeToStepFormValue(ummeldung(), bisherigerVertragspartnerMode));
  const isBisherigerVertragspartnerDraftValid = isDraftValid(
    bisherigerVertragspartnerDraft,
    bisherigerVertragspartnerStepSchema(bisherigerVertragspartnerMode),
    bisherigerVertragspartnerStepPflichtfelderSchema(bisherigerVertragspartnerMode),
  );

  const neuerVertragspartnerDraft = computed(() => mapNeuerKundeToStepFormValue(ummeldung(), store));
  const isNeuerVertragspartnerDraftValid = isDraftValid(
    neuerVertragspartnerDraft,
    ...(store.isZaehlpunktAktiv()
      ? [neuerVertragspartnerAktiverZaehlpunktSchema, neuerVertragspartnerAktiverZaehlpunktPflichtfelderSchema]
      : [neuerVertragspartnerInaktiverZaehlpunktSchema, neuerVertragspartnerInaktiverZaehlpunktPflichtfelderSchema]),
  );

  return computed(() => {
    const bisherigerVertragspartner = bisherigerVertragspartnerDraft();
    return {
      produkt: isProduktDraftValid() && isProduktStepComplete(ummeldung()),
      datum: isDatumDraftValid() && isDatumStepComplete(ummeldung()),
      bisherigerVertragspartner:
        isBisherigerVertragspartnerDraftValid() &&
        (bisherigerVertragspartner.vertragspartner.vertragspartnerBekanntheit === 'unknown' || !!bisherigerVertragspartner.unterschrift),
      neuerVertragspartner: isNeuerVertragspartnerDraftValid() && !!neuerVertragspartnerDraft().unterschrift,
    };
  });
}

export function formatZaehlpunktAdresse(ummeldung: Ummeldung | null): string {
  return getAddressStreetAndPostCodeFormatted(mapUmmeldungAdresseToAddress(ummeldung?.produktdaten?.at(0)?.adresse));
}
